import React, { useState, useEffect, useCallback } from 'react';
import './AnalyticalPortrait.css';

const SECTION_ICONS = {
  brain: '🧠',
  heart: '❤️',
  users: '👥',
  compass: '🧭',
  message: '💬',
  layers: '🔮',
};

function AnalyticalPortrait({ isOpen, onClose, token }) {
  const [portraits, setPortraits] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);
  const [toast, setToast] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || '';

  const showToast = useCallback((text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchPortraits = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/portraits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load portraits');
      const data = await res.json();
      setPortraits(data.portraits || []);
      if (data.portraits && data.portraits.length > 0 && !selectedId) {
        setSelectedId(data.portraits[0].id);
      }
    } catch (err) {
      console.error('Error loading portraits:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBase, selectedId]);

  useEffect(() => {
    if (isOpen) {
      fetchPortraits();
      setExpandedKey(null);
    }
  }, [isOpen, fetchPortraits]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${apiBase}/portraits/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Generation failed');
      }
      const portrait = await res.json();
      setPortraits(prev => [portrait, ...prev]);
      setSelectedId(portrait.id);
      setExpandedKey(null);
      showToast('Portrait generated successfully');
    } catch (err) {
      showToast(err.message || 'Generation failed', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${apiBase}/portraits/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      setPortraits(prev => prev.filter(p => p.id !== id));
      if (selectedId === id) {
        const remaining = portraits.filter(p => p.id !== id);
        setSelectedId(remaining.length > 0 ? remaining[0].id : null);
      }
      showToast('Portrait deleted');
    } catch (err) {
      showToast('Failed to delete portrait', 'error');
    }
  };

  if (!isOpen) return null;

  const currentPortrait = portraits.find(p => p.id === selectedId);
  const sections = currentPortrait?.sections || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="ap-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ap-panel">
        {toast && <div className={`ap-toast ${toast.type}`}>{toast.text}</div>}

        {/* Branded Header */}
        <div className="ap-header-banner">
          <div className="ap-header-top">
            <div className="ap-brand">
              <div className="ap-brand-icon">🔬</div>
              <div>
                <h2 className="ap-title">Analytical Portrait</h2>
                <div className="ap-title-sub">Heart Felt Souls</div>
              </div>
            </div>
            <button className="ap-close" onClick={onClose}>&times;</button>
          </div>
          <p className="ap-subtitle">
            Deep personality analysis and pattern insights drawn from your memories and soul profile.
          </p>
        </div>

        {/* Controls */}
        <div className="ap-controls">
          <div className="ap-meta">
            {currentPortrait && (
              <>
                <div className="ap-meta-item">
                  Memories: <span className="ap-meta-value">{currentPortrait.memory_count || 0}</span>
                </div>
                <div className="ap-meta-item">
                  Soul Score: <span className="ap-meta-value">{currentPortrait.soul_profile_version || '—'}</span>
                </div>
                <div className="ap-meta-item">
                  Generated: <span className="ap-meta-value">{formatDate(currentPortrait.generated_at)}</span>
                </div>
              </>
            )}
          </div>
          <div className="ap-actions">
            <button className="ap-generate-btn" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Analyzing…' : portraits.length > 0 ? 'Generate New' : 'Generate Portrait'}
            </button>
            {currentPortrait && (
              <button className="ap-delete-btn" onClick={() => handleDelete(currentPortrait.id)}>
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="ap-content-area">
          {generating && (
            <div className="ap-generating">
              <div className="ap-generating-orb" />
              <div className="ap-generating-text">Analyzing your soul dimensions…</div>
              <div className="ap-generating-hint">Building 6 analytical sections from your memories and traits. This may take a minute.</div>
            </div>
          )}

          {loading && !generating && (
            <div className="ap-loading">Loading your portraits…</div>
          )}

          {/* Empty State */}
          {!generating && !loading && portraits.length === 0 && (
            <div className="ap-empty">
              <div className="ap-empty-visual">🔬</div>
              <div className="ap-empty-title">Discover your inner landscape</div>
              <p>Click <strong>Generate Portrait</strong> to create a deep personality analysis from your memories, soul profile, and wisdom.</p>
            </div>
          )}

          {/* Portrait View */}
          {!generating && currentPortrait && (
            <>
              {/* History selector */}
              {portraits.length > 1 && (
                <div className="ap-history">
                  <span className="ap-history-label">Portrait:</span>
                  <select value={selectedId || ''} onChange={(e) => { setSelectedId(Number(e.target.value)); setExpandedKey(null); }}>
                    {portraits.map(p => (
                      <option key={p.id} value={p.id}>
                        {formatDate(p.generated_at)} — {p.memory_count} memories
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Summary */}
              {currentPortrait.summary && (
                <div className="ap-summary">{currentPortrait.summary}</div>
              )}

              {/* Sections */}
              <div className="ap-sections">
                {sections.map(sec => {
                  const isExpanded = expandedKey === sec.key;
                  return (
                    <div
                      key={sec.key}
                      className={`ap-section ${isExpanded ? 'ap-expanded' : ''}`}
                      data-icon={sec.icon}
                    >
                      <div className="ap-section-header" onClick={() => setExpandedKey(isExpanded ? null : sec.key)}>
                        <div className="ap-section-icon">{SECTION_ICONS[sec.icon] || '📊'}</div>
                        <div className="ap-section-title">{sec.title}</div>
                        <span className="ap-expand-icon">{isExpanded ? '▾' : '▸'}</span>
                      </div>

                      {isExpanded && (
                        <div className="ap-section-body">
                          <div className="ap-section-content">
                            {sec.content.split('\n').map((p, i) => p.trim() ? <p key={i}>{p}</p> : null)}
                          </div>
                          {sec.evidence && sec.evidence.length > 0 && (
                            <div className="ap-evidence">
                              <div className="ap-evidence-label">Supporting Evidence</div>
                              {sec.evidence.map((quote, i) => (
                                <div key={i} className="ap-evidence-item">"{quote}"</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="ap-footer">
          Powered by <span className="ap-footer-brand">Heart Felt Souls</span> — understanding who you are.
        </div>
      </div>
    </div>
  );
}

export default AnalyticalPortrait;
